import { clsx } from 'clsx';
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import { ESearchFilters } from '@/constants/searchFilters';
import {
  RV_CAMPSITE_BUNDLES_LANDING_PAGE,
  RV_CAMPSITE_BUNDLES_RESORT_PAGE,
  RV_CAMPSITE_CATEGORY_PAGE,
  RV_DELIVERY_HOMEPAGE,
} from '@/constants/urls';
import { useAppLayout } from '@/context/AppLayoutContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useIsLocalityPage } from '@/hooks/useIsLocalityPage';
import { useIsSearchPage } from '@/hooks/useIsSearchPage';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { useGetWishlists } from '@/hooks/useWishlists';
import { selectAuthenticated } from '@/redux/modules/auth/selectors';
import { fetchAllowedStatesForStay } from '@/redux/modules/stayStates';
import { setWishlistedItems } from '@/redux/modules/wishlists';
import { ERentalType } from '@/services/analytics/types';
import { useListing20IsEnabled } from '@/services/experiments/listing20';
import { useSerpGuidedSearchDecision } from '@/services/experiments/serpGuidedSearch';
import { EWishlistRoles } from '@/utility/wishlists';

import { Impersonated } from '../auth/Impersonated/Impersonated';
import GlobalHeader from '../ui/GlobalHeader/GlobalHeader';
import Footer from './footer/Footer';
import { PromoBanner } from './PromoBanner';
import { SitewideBanner } from './SitewideBanner';
import SystemDowntimeNotice from './SystemDowntimeNotice';

const LoginModalContainer = lazy(() => import('@/components/auth/LoginModalContainer'));
const SignupModalContainer = lazy(() => import('@/components/auth/SignupModalContainer'));
const HelpMenuContainer = lazy(() =>
  import('./help-menu').then(module => ({ default: module.HelpMenuContainer })),
);

const HIDDEN_HEADER_AND_FOOTER_ROUTES = [
  '/rv-rental/[location]/[slug]/photos',
  '/rv-rental/[location]/[slug]/reviews',
  '/stay-rental/[location]/[slug]/photos',
  '/stay-rental/[location]/[slug]/reviews',
  '/campsite-rental/[location]/[slug]/photos',
  '/campsite-rental/[location]/[slug]/reviews',
  '/auto/car-rental/[location]/[slug]/photos',
  '/auto/car-rental/[location]/[slug]/reviews',
  '/pro/[slug]/photos',
  '/pro/[slug]/reviews',
  '/pro/[slug]/reviews/[page]',
  '/trips/[slug]/cancel',
  '/auto/trips/[slug]/cancel',
  '/campground-rental/[location]/[slug]/photos',
  '/campground-rental/[location]/[slug]/reviews',
  '/destinations/outdoorsy-hill-country',
  '/destinations/outdoorsy-hill-country/terms-and-conditions',
  '/guided-search',
  '/escapes/[slug]/photos',
  '/escapes/[slug]/reviews',
];

const CHECKOUT_ROUTES = ['/checkout', '/auto/checkout'];
const HIDDEN_FOOTER_START_WITH_ROUTES = ['/auto/car-search', ...CHECKOUT_ROUTES];
const LISTING_ROUTES = ['/rv-rental/[location]/[slug]'];

const MOBILE_HIDDEN_HEADER_AND_FOOTER_ROUTES = [
  '/trips/[slug]',
  '/auto/trips/[slug]',
  '/rv-rental/[location]/[slug]',
  '/auto/car-rental/[location]/[slug]',
  '/account/[id]/edit',
];

const FULL_PAGE_CANVAS_ROUTES = [
  '/',
  RV_DELIVERY_HOMEPAGE,
  '/auto',
  RV_CAMPSITE_BUNDLES_LANDING_PAGE,
  '/stationary-delivery',
  '/invite/b/[code]',
  '/generations-in-the-wild',
  RV_CAMPSITE_BUNDLES_RESORT_PAGE,
  RV_CAMPSITE_CATEGORY_PAGE,
];

const HIDDEN_NAV_SEARCH = ['/locality', '/locality-directory-by-type'];

const FLUID_PAGE_ROUTES = [
  PAGE_PATHNAME[EPage.SEARCH],
  PAGE_PATHNAME[EPage.ESCAPES_SEARCH],
  PAGE_PATHNAME[EPage.CAMPGROUND_SEARCH],
  ...CHECKOUT_ROUTES,
  '/auto/car-search',
  '/guide/seasonal/summer',
  '/guide/seasonal/fall',
  '/guide/seasonal/winter',
  '/wishlists/[id]',
];

const TRANSPARENT_HEADER_ROUTES = ['/auto'];
const TRANSPARENT_HEADER_ROUTES_ONLY_DESKTOP = ['/gift-cards'];
const TRANSPARENT_HEADER_START_WITH_ROUTES = [
  '/guide/seasonal/summer',
  '/guide/seasonal/fall',
  '/guide/seasonal/winter',
];

const NON_STICKY_HEADER_START_WITH_ROUTES = [
  '/guide/seasonal/summer',
  '/guide/seasonal/fall',
  '/gift-cards',
  '/guide/seasonal/winter',
];

const DARK_NAVIGATION = ['/gift-cards'];

const WHITE_HEADER_ROUTES = ['/about/outdoorsy-group', '/become-a-host/auto', '/auto'];
const WHITE_HEADER_MOBILE_ROUTES = ['/gift-cards'];

const ApplicationLayout = ({ children, pageProps }) => {
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const { isSearchPage } = useIsSearchPage();
  const { isLocalityPage, isLocalityDirectoryByTypePage } = useIsLocalityPage();

  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectAuthenticated);

  const isNewLayout =
    pageProps.isEscapesPage ||
    pageProps.isHomePage ||
    pageProps.isAutoHomePage ||
    pageProps.isRvDeliveryPage ||
    pageProps.isNewPromoPage;
  const isAutoPage = pageProps.isAutoPage || router.pathname.startsWith('/auto');

  const serpGuidedSearchEnabled =
    useSerpGuidedSearchDecision().enabled && router.pathname.startsWith('/locality');

  const queryParams = useQueryParams();
  const [showSearchBar, setShowSearchBar] = useState(false);

  const isCheckout = CHECKOUT_ROUTES.some(route => router.pathname.startsWith(route));

  const listing20IsEnabled = useListing20IsEnabled();
  const isListingV2 =
    LISTING_ROUTES.some(route => router.pathname.startsWith(route)) && listing20IsEnabled;

  const isSignatureCampgroundBookASite =
    router.pathname.startsWith('/campground-rental') && queryParams.signature === 'true';

  const shouldHideHeaderAndFooter =
    isSignatureCampgroundBookASite ||
    HIDDEN_HEADER_AND_FOOTER_ROUTES.includes(pageProps.pathname || router.pathname) ||
    (isMobile &&
      MOBILE_HIDDEN_HEADER_AND_FOOTER_ROUTES.includes(pageProps.pathname || router.pathname));

  const shouldHideFooter =
    shouldHideHeaderAndFooter ||
    HIDDEN_FOOTER_START_WITH_ROUTES.some(route => router.pathname.startsWith(route));

  const mobileShouldHideHeaderAndFooter = MOBILE_HIDDEN_HEADER_AND_FOOTER_ROUTES.includes(
    pageProps.pathname || router.pathname,
  );

  const isFluidPage = FLUID_PAGE_ROUTES.includes(router.pathname) || isLocalityPage;

  const isFullCanvasPage =
    FULL_PAGE_CANVAS_ROUTES.includes(pageProps.pathname || router.pathname) ||
    pageProps.isNewPromoPage;
  const headerIsTransparent =
    TRANSPARENT_HEADER_ROUTES.includes(pageProps.pathname || router.pathname) ||
    TRANSPARENT_HEADER_START_WITH_ROUTES.some(route => router.pathname.startsWith(route));

  const headerDesktopTransparent =
    !isMobile && TRANSPARENT_HEADER_ROUTES_ONLY_DESKTOP.includes(router.pathname);

  const isBaseSearchPage =
    router.pathname.startsWith('/rv-search') ||
    router.pathname.startsWith('/auto/car-search') ||
    router.pathname.startsWith('/escapes-search') ||
    router.pathname.startsWith('/campground-search');
  const hideSearchBar = HIDDEN_NAV_SEARCH.includes(pageProps.pathname || router.pathname);
  const hideSearchNavbar = isNewLayout || hideSearchBar || !isBaseSearchPage;
  const noSearchNavbar =
    !(isLocalityPage && !isLocalityDirectoryByTypePage) &&
    (serpGuidedSearchEnabled ? !showSearchBar : hideSearchNavbar);

  const { data: wishlists } = useGetWishlists({
    isAuthenticated,
    examplesOnly: false,
    excludeExamples: true,
  });

  useEffect(() => {
    // We use SWR to fetch wishlists, but in order to keep using the funtionality build around favorites,
    // we need to keep the redux store updated with the wishlisted rentals
    if (wishlists && isAuthenticated) {
      const wishlistedRentals = wishlists.flatMap(wishlist =>
        wishlist?.rentals?.map(rental_id => ({
          id: wishlist.id,
          item_id: rental_id,
          item_type: 'rental',
          wishlist_creator_id: wishlist?.user_details?.find(
            user => user.user_role === EWishlistRoles.Owner,
          )?.id,
          wishlist_name: wishlist.title,
          wishlist_id: wishlist.id,
        })),
      );
      const wishlistedEscapes = wishlists.flatMap(wishlist =>
        wishlist?.campgrounds?.map(campground_id => ({
          id: wishlist.id,
          item_id: campground_id,
          item_type: 'escape',
          wishlist_creator_id: wishlist?.user_details?.find(
            user => user.user_role === EWishlistRoles.Owner,
          )?.id,
          wishlist_name: wishlist.title,
          wishlist_id: wishlist.id,
        })),
      );
      dispatch(setWishlistedItems({ rentals: wishlistedRentals, escapes: wishlistedEscapes }));
    } else {
      dispatch(setWishlistedItems({ rentals: [], escapes: [] }));
    }
  }, [wishlists, isAuthenticated, dispatch]);

  useEffect(() => {
    if (!serpGuidedSearchEnabled) return;

    const handleScroll = () => {
      const heroSection = document.getElementById('serp-hero-section');
      setShowSearchBar((heroSection.getBoundingClientRect().bottom || 0) < 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [serpGuidedSearchEnabled]);

  const isDarkNavigation =
    !isMobile && DARK_NAVIGATION.some(route => router.pathname.startsWith(route));

  const isHeaderWhite =
    WHITE_HEADER_ROUTES.includes(pageProps.pathname || router.pathname) ||
    WHITE_HEADER_ROUTES.some(route => router.pathname.startsWith(route)) ||
    (isMobile && WHITE_HEADER_MOBILE_ROUTES.includes(router.pathname));

  const isRv =
    !router.query[ESearchFilters.RENTAL_CATEGORY] ||
    router.query[ESearchFilters.RENTAL_CATEGORY] === ERentalType.RV;

  const alwaysStickyHeaders =
    isMobile && (isSearchPage || serpGuidedSearchEnabled || isLocalityPage) && isRv;
  const nonStickyHeader = NON_STICKY_HEADER_START_WITH_ROUTES.some(route =>
    router.pathname.startsWith(route),
  );

  const hasStickyHeader = !nonStickyHeader && !shouldHideHeaderAndFooter;
  const hasStuckHeaderShadow = isNewLayout && hasStickyHeader;

  const stuckHeaderCheckRef = useRef(null);
  const [headerIsStuck, setHeaderIsStuck] = useState(false);

  const { setHeaderHeight } = useAppLayout();
  const headerRef = useRef(null);

  useEffect(() => {
    if (!hasStuckHeaderShadow || !stuckHeaderCheckRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setHeaderIsStuck(!!entry && entry.intersectionRatio < 1),
      { rootMargin: '1px 0px 0px 0px', threshold: [1] },
    );

    observer.observe(stuckHeaderCheckRef.current);

    return () => observer.disconnect();
  }, [hasStuckHeaderShadow]);

  useEffect(() => {
    if (!headerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setHeaderHeight(entry.contentRect.height);
      }
    });

    observer.observe(headerRef.current);

    return () => observer.disconnect();
  }, [setHeaderHeight]);

  useEffect(() => {
    dispatch(fetchAllowedStatesForStay());
  }, [dispatch]);

  return (
    <>
      <Suspense fallback={null}>
        <SignupModalContainer isAutoPage={isAutoPage} />
      </Suspense>
      <Suspense fallback={null}>
        <LoginModalContainer />
      </Suspense>
      <Suspense fallback={null}>
        <HelpMenuContainer isAutoPage={isAutoPage} />
      </Suspense>

      <div className="font-sans" id="app">
        {hasStuckHeaderShadow && <div ref={stuckHeaderCheckRef} />}

        <div
          id="global-headers"
          ref={headerRef}
          className={clsx(
            'relative',
            !nonStickyHeader &&
              !shouldHideHeaderAndFooter && {
                'sticky top-0 z-1299': alwaysStickyHeaders,
                'md:sticky md:top-0 md:z-1299': !alwaysStickyHeaders && !isCheckout,
                'lg:sticky lg:top-0 lg:z-1299': !alwaysStickyHeaders && isCheckout,
                'shadow-500': headerIsStuck && alwaysStickyHeaders,
                'md:shadow-500': headerIsStuck && !alwaysStickyHeaders && !isCheckout,
                'lg:shadow-500': headerIsStuck && !alwaysStickyHeaders && isCheckout,
              },
          )}>
          <Impersonated />
          <SystemDowntimeNotice />
          <SitewideBanner />

          <div
            className={clsx({
              'hidden md:block': mobileShouldHideHeaderAndFooter,
            })}>
            {!shouldHideHeaderAndFooter && (
              <GlobalHeader
                isHomePage={pageProps.isHomePage}
                isEscapesPage={pageProps.isEscapesPage}
                isRvDeliveryPage={pageProps.isRvDeliveryPage}
                isAutoHomePage={pageProps.isAutoHomePage}
                isNewPromoPage={pageProps.isNewPromoPage}
                isSERP={isSearchPage}
                isAutoPage={isAutoPage}
                noSearchNavbar={noSearchNavbar}
                isFluidPage={isFluidPage}
                isTransparent={headerIsTransparent || headerDesktopTransparent}
                isSticky={isMobile ? alwaysStickyHeaders : !nonStickyHeader}
                isHeaderWhite={isHeaderWhite}
                isDarkNavigation={isDarkNavigation}
                isCheckout={isCheckout}
                isListingV2={isListingV2}
              />
            )}

            {pageProps.isNewPromoPage && (
              <PromoBanner bannerText={pageProps.banner.text} bannerIcon={pageProps.banner.icon} />
            )}
          </div>
        </div>

        <div
          style={isNewLayout ? { backgroundColor: 'var(--page-content-bg-color)' } : {}}
          className={clsx('relative min-h-[60vh]', {
            'transition-[background-color] duration-700 ease-in-out': isNewLayout,
            'bg-canvas-100': isFullCanvasPage,
          })}>
          {children}
        </div>

        <div
          className={clsx({
            'hidden md:block': mobileShouldHideHeaderAndFooter,
          })}>
          {!shouldHideFooter && <Footer isNewLayout={isNewLayout} isAutoPage={isAutoPage} />}
        </div>
      </div>
    </>
  );
};

export default ApplicationLayout;
